import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _capitalize from 'lodash/capitalize';
import _lowerCase from 'lodash/lowerCase';
import _startCase from 'lodash/startCase';

import { useSelector } from 'react-redux';

import { DurationTicker } from 'components/DurationTicker';
import { getActiveUncategorizedInterval } from 'lib/selectors/getActiveUncategorizedInterval';
import { getActiveCategorizedInterval } from 'lib/selectors/getActiveCategorizedInterval';
import Routes from 'lib/utils/routes';
import { MACHINE_STATUSES } from 'lib/constants';
import { exclamationCircle, minus } from 'lib/icons';
import { getStatusLabel } from 'lib/selectors/getStatusLabel';
import { getWorkOrderFlowEnabled } from 'lib/selectors/getWorkOrderFlowEnabled';
import { getMachine } from 'lib/selectors/getMachine';
import { getAreActivitiesEnabled } from 'lib/selectors/getAreActivitiesEnabled';
import { getLatestJob } from 'lib/selectors/getLatestJob';
import { getWorkOrderId } from 'lib/selectors/getWorkOrderId';
import { getIsLatestActivitySetOpen } from 'lib/selectors/getIsLatestActivitySetOpen';
import { toISO, now } from 'lib/utils/date';
// import { ActiveShimmer } from 'components/ActiveShimmer';

import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getMachineHeaderState } from 'lib/selectors/getMachineHeaderState';
import { getMachineHeaderVariant } from 'lib/selectors/getMachineHeaderVariant';
import { getMachineHeaderTime } from 'lib/selectors/getMachineHeaderTime';
import { getIsOperatorTrackingEnabled } from 'lib/selectors/getIsOperatorTrackingEnabled';
import {
  Button,
  MachineStatusIcon,
  Text,
  TouchTarget,
  TouchTargetPosition,
  MachineAndOperatorNameContainer,
  TouchPointArea,
} from './styled';

import Layout from './Layout';
import { MachineHeaderBottomLeft } from './MachineHeaderBottomLeft';

function labelFor(t, status) {
  switch (status) {
    case MACHINE_STATUSES.UNAVAILABLE:
      return _startCase(t(_lowerCase('machine connection error')));
    default:
      return _startCase(t(_lowerCase(status)));
  }
}

function getMachineStatusIcon(status) {
  switch (status) {
    case MACHINE_STATUSES.UNAVAILABLE:
      return exclamationCircle;
    default:
      return minus;
  }
}

const MachineHeader = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [touchPointRight, setTouchPointRight] = useState(0);
  const machine = useSelector(getMachine);
  const operator = useSelector(getCurrentOperator);
  const status = useSelector(getStatusLabel);
  const areActivitiesEnabled = useSelector(getAreActivitiesEnabled);
  const job = useSelector(getLatestJob);
  const workOrderId = useSelector(getWorkOrderId);
  const isLatestActivitySetOpen = useSelector(getIsLatestActivitySetOpen);

  const state = useSelector(getMachineHeaderState);
  const variant = useSelector(getMachineHeaderVariant);
  const workOrderFlowEnabled = useSelector(getWorkOrderFlowEnabled);
  // The active home tab — the header renders inside the machine page's
  // home/:tab route, and the ERP overlay path nests beneath it.
  const { tab } = useParams();
  const time = useSelector(getMachineHeaderTime);
  const isOperatorTrackingEnabled = useSelector(getIsOperatorTrackingEnabled);

  const currentUncategorizedInterval = useSelector(
    getActiveUncategorizedInterval
  );
  const currentCategorizedInterval = useSelector(getActiveCategorizedInterval);

  const onClickToOverview = () => {
    // if machine has error / unavailable, will disbale touchpoint and work order edit
    if (variant !== 'unavailable') {
      // ERP flow: the current-job overview is an overlay nested under the
      // active home tab so the machine page stays mounted beneath it (and
      // closing returns to the same tab). The legacy JobInfo is a full page
      // on the un-nested path.
      const pathname =
        workOrderFlowEnabled && tab
          ? Routes.machineIdHomeTabCurrentOperationInfoPath(machine.id, tab)
          : Routes.machineIdCurrentOperationInfoPath(machine.id);
      return history.push({
        pathname,
        state: {
          job,
          isStartingNewRun: true,
          workOrderId,
        },
      });
    }
    return null;
  };

  const machineAndOperatorNameText = !isOperatorTrackingEnabled
    ? machine.name
    : `${machine.name} | ${operator?.name ?? t('No Operator').toUpperCase()}`;

  return (
    <>
      {isLatestActivitySetOpen && (
        <>
          <TouchPointArea
            width={touchPointRight}
            id="touch-point"
            onTouchEnd={onClickToOverview}
          ></TouchPointArea>
        </>
      )}
      <Layout
        variant={variant}
        TopLeft={
          <MachineAndOperatorNameContainer variant={variant}>
            {machineAndOperatorNameText}
          </MachineAndOperatorNameContainer>
        }
        TopRight={
          currentUncategorizedInterval ? (
            <>
              <Text variant={variant} categorize>
                {`${_capitalize(t('uncategorized'))} : `}
                <DurationTicker
                  startDate={currentUncategorizedInterval.start}
                />
              </Text>
              <TouchTargetPosition>
                <TouchTarget
                  onClick={() => {
                    return history.push({
                      pathname: Routes.machineIdCategorizePath(machine.id),
                      state: {
                        preselected: currentUncategorizedInterval,
                        returnPath: history.location.pathname,
                      },
                    });
                  }}
                >
                  <Button
                    id="categorize"
                    variant="auxiliary"
                    headerVariant={variant}
                  >
                    {t('categorize|full')}
                  </Button>
                </TouchTarget>
              </TouchTargetPosition>
            </>
          ) : currentCategorizedInterval ? (
            // filter out "artificial" setup annos for companies using jsgs
            (areActivitiesEnabled || !currentCategorizedInterval.setup) && (
              <>
                <Text variant={variant} categorize>
                  {currentCategorizedInterval.annotationType.name}:{' '}
                  <DurationTicker
                    startDate={currentCategorizedInterval.start}
                  />
                </Text>
                <TouchTargetPosition>
                  <TouchTarget
                    onClick={() => {
                      return history.push({
                        pathname: Routes.machineIdCategorizePath(machine.id),
                        state: {
                          preselected: currentCategorizedInterval,
                          splitting: true,
                          splitTimestamp: toISO(now()),
                          returnPath: history.location.pathname,
                        },
                      });
                    }}
                  >
                    <Button
                      id="split-downtime"
                      variant="auxiliary"
                      headerVariant={variant}
                    >
                      {t('split downtime')}
                    </Button>
                  </TouchTarget>
                </TouchTargetPosition>
              </>
            )
          ) : null
        }
        BottomLeft={
          <MachineHeaderBottomLeft
            variant={variant}
            onClickToOverview={onClickToOverview}
            setTouchPointRight={setTouchPointRight}
          />
        }
        BottomRight={
          <>
            <Text variant={variant} noWrap>
              {labelFor(t, state)} :&nbsp;
              {time && <DurationTicker startDate={time} />}
            </Text>
            {status === MACHINE_STATUSES.ACTIVE ? (
              // <ActiveShimmer status={variant} />
              <span />
            ) : (
              <MachineStatusIcon
                variant={variant}
                icon={getMachineStatusIcon(status)}
              />
            )}
          </>
        }
      />
    </>
  );
};

export { MachineHeader };
